/**
 * Search dismissal — close the suggestions, keep what was typed.
 *
 * Two result panels can be open over this store and neither dismissed correctly:
 *
 *  1. The Globo Smart Filter app attaches a jQuery-UI autocomplete to every `input[name="q"]` and
 *     appends its menu to <body>. It has no outside-click close at all, so the suggestion list stays
 *     over the page while the shopper browses.
 *
 *  2. The theme's own predictive panel. Its ESC and overlay-click paths hide the overlay without
 *     telling `predictive-search`, so a stale result list is still `open` on reopen — and the one
 *     path that does notify it (`[data-close-search]`, which is hidden above 768px) calls
 *     `close(true)`, wiping the query. Note this store currently renders no header search trigger
 *     at all (`[data-open-search-popup]` matches nothing), so that panel is unreachable markup —
 *     this half is defensive, for if the icon is re-enabled.
 *
 * Both are closed WITHOUT touching `input.value`: the query stays in the box so the shopper can
 * carry on from where they were.
 *
 * The app's menu carries `display: block !important` and the app bundles its own jQuery, so the
 * widget instance is unreachable from here and its own close() could not have beaten that rule
 * either. The mechanism is therefore an important-priority inline hide, released again on the next
 * keystroke so suggestions can re-open. See closeAppAutocomplete/releaseAppAutocomplete.
 */
(() => {
  if (window.__mexSearchUx) return;
  window.__mexSearchUx = true;

  const APP_MENU = "ul.ui-autocomplete";
  const APP_INPUT = "input.ui-autocomplete-input";
  const SEARCH_INPUT = `${APP_INPUT}, input[name="q"], [data-search-input]`;
  const SAFE = [APP_MENU, APP_INPUT, 'input[name="q"]', "[data-search-input]", "predictive-search", "[data-search-container]"];

  const isAppMenuOpen = () =>
    [...document.querySelectorAll(APP_MENU)].some((m) => {
      if (window.getComputedStyle(m).display === "none") return false;
      const rect = m.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

  /**
   * Measured on this store: the app ships `display: block !important` for its menu, and it runs its
   * own bundled jQuery — `window.jQuery.fn.autocomplete` is undefined, so the widget instance is not
   * reachable from here and even the widget's own close() could not beat that rule. Hence an
   * important-priority inline hide, which an author !important cannot override.
   *
   * The widget API is still attempted first, for the case where a future app build exposes it.
   */
  const closeAppAutocomplete = () => {
    const jq = window.jQuery || window.$;
    if (jq && jq.fn && jq.fn.autocomplete) {
      document.querySelectorAll(APP_INPUT).forEach((input) => {
        const $input = jq(input);
        const widget = $input.data("ui-autocomplete") || $input.data("uiAutocomplete") || $input.data("autocomplete");
        if (!widget) return;
        try {
          $input.autocomplete("close");
        } catch (err) {
          /* the inline hide below is the real mechanism anyway */
        }
      });
    }
    document.querySelectorAll(APP_MENU).forEach((menu) => {
      menu.style.setProperty("display", "none", "important");
      menu.setAttribute("aria-hidden", "true");
    });
  };

  /**
   * Release our hide so the app can show suggestions again on the next keystroke. Only the
   * declaration we set is removed (identified by its `important` priority), so the widget's own
   * inline display state is never clobbered.
   */
  const releaseAppAutocomplete = () => {
    document.querySelectorAll(APP_MENU).forEach((menu) => {
      if (menu.style.getPropertyPriority("display") !== "important") return;
      menu.style.removeProperty("display");
      menu.removeAttribute("aria-hidden");
    });
  };

  // close(false) keeps the term — see assets/predictive-search.js.
  const closeThemePredictive = () => {
    const panel = document.querySelector("predictive-search");
    if (panel && typeof panel.close === "function") panel.close(false);
  };

  const closeAll = () => {
    closeAppAutocomplete();
    closeThemePredictive();
  };

  const isInsideSearchUi = (target) => target instanceof Element && SAFE.some((sel) => target.closest(sel));

  const onPointerDown = (event) => {
    if (!isAppMenuOpen() && !document.querySelector("predictive-search[open]")) return;
    if (isInsideSearchUi(event.target)) return;
    closeAll();
  };

  // Capture + pointerdown: the app re-opens its menu on some click handlers, so acting on the way
  // down (and before those handlers) is what makes the dismissal stick.
  document.addEventListener("mousedown", onPointerDown, true);
  document.addEventListener("touchstart", onPointerDown, true);

  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Escape") return;
      const target = event.target instanceof Element ? event.target : null;
      const inPageAppInput = target && target.closest(APP_INPUT) && !target.closest("[data-search-popup]");

      // Two things would otherwise wipe the field on Escape, and both must be stopped:
      //   • the browser's native behaviour on <input type="search"> (the collection bar) — clears it,
      //     and the synthetic input event that follows would re-arm the menu we just hid;
      //   • jQuery-UI's "revert the field to the last searched term", which empties it too.
      // Taken over ONLY for the in-page search bars: the header popup's Escape still has to reach
      // the theme so its overlay closes, and every other Escape handler on the page is left alone.
      if (inPageAppInput) {
        event.preventDefault();
        event.stopPropagation();
      }
      closeAll();
    },
    true
  );

  // The theme moves [data-search-popup] to <body> on boot, so this is delegated rather than bound.
  document.addEventListener(
    "click",
    (event) => {
      if (event.target instanceof Element && event.target.matches("[data-search-popup]")) closeThemePredictive();
    },
    true
  );

  // Re-arm: typing in (or returning to) a search field must be able to show suggestions again.
  const rearm = (event) => {
    if (event.target instanceof Element && event.target.closest(SEARCH_INPUT)) releaseAppAutocomplete();
  };
  document.addEventListener("input", rearm, true);
  document.addEventListener("focusin", rearm, true);
})();
